import { Minus, Plus } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { UsDateInput } from '@/components/shared/UsDateInput';

interface FilterCategoryProps {
  readonly label: string;
  readonly defaultOpen?: boolean;
  /** Keeps the category open regardless of the toggle, used while it holds an error. */
  readonly forceOpen?: boolean;
  readonly children: ReactNode;
}

/** One independently expandable category of the filter panel (US-014 AC-002). */
export function FilterCategory({
  label,
  defaultOpen = false,
  forceOpen = false,
  children,
}: FilterCategoryProps): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  // A category holding a validation message cannot be collapsed: Search is disabled while it stands,
  // and hiding the reason would leave the provider with a dead button and no explanation.
  const expanded = open || forceOpen;

  return (
    <div className="py-3">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        disabled={forceOpen}
        onClick={(): void => {
          setOpen((current) => !current);
        }}
        className="text-ink flex w-full items-center justify-between text-sm font-semibold"
      >
        {label}
        {expanded ? (
          <Minus aria-hidden="true" className="text-muted h-4 w-4" />
        ) : (
          <Plus aria-hidden="true" className="text-muted h-4 w-4" />
        )}
      </button>

      <div id={contentId} hidden={!expanded} className="mt-2.5">
        {children}
      </div>
    </div>
  );
}

interface FilterTextProps {
  readonly label: string;
  readonly value: string;
  readonly inputMode?: 'decimal';
  readonly error?: string | null;
  onChange: (value: string) => void;
}

export function FilterText({
  label,
  value,
  inputMode,
  error = null,
  onChange,
}: FilterTextProps): React.JSX.Element {
  const errorId = useId();

  return (
    <FilterCategory label={label} defaultOpen={value.length > 0} forceOpen={error !== null}>
      <input
        type="text"
        aria-label={label}
        value={value}
        inputMode={inputMode}
        aria-invalid={error !== null}
        aria-describedby={error === null ? undefined : errorId}
        onChange={(event): void => {
          onChange(event.target.value);
        }}
        className="border-line focus:border-brand-600 h-9 w-full rounded-md border bg-surface-muted px-2.5 text-[13px] focus:bg-surface"
      />
      {error !== null && (
        <p id={errorId} role="alert" className="text-destructive mt-1.5 text-xs">
          {error}
        </p>
      )}
    </FilterCategory>
  );
}

interface FilterDateRangeProps {
  readonly label: string;
  readonly from: string;
  readonly to: string;
  readonly error: string | null;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
}

export function FilterDateRange({
  label,
  from,
  to,
  error,
  onChangeFrom,
  onChangeTo,
}: FilterDateRangeProps): React.JSX.Element {
  const errorId = useId();
  // The two ends share every attribute but their value, so they share one component. The pickers are
  // deliberately unconstrained (no min/max): a date outside the loaded claims' window can still be
  // typed or pasted, and the range rules in claim-filters report it with a message instead.
  const end = { invalid: error !== null, errorId };

  return (
    <FilterCategory
      label={label}
      defaultOpen={from.length > 0 || to.length > 0}
      forceOpen={error !== null}
    >
      <div className="flex flex-col gap-2">
        <DateEnd {...end} label={`${label} from`} value={from} onChange={onChangeFrom} />
        <DateEnd {...end} label={`${label} to`} value={to} onChange={onChangeTo} />
        {error !== null && (
          <p id={errorId} role="alert" className="text-destructive text-xs">
            {error}
          </p>
        )}
      </div>
    </FilterCategory>
  );
}

interface DateEndProps {
  readonly label: string;
  readonly value: string;
  readonly invalid: boolean;
  readonly errorId: string;
  onChange: (value: string) => void;
}

/** One end of a date range. Never constrained by the picker itself; the range rules validate it (PP-011). */
function DateEnd({
  label,
  value,
  invalid,
  errorId,
  onChange,
}: DateEndProps): React.JSX.Element {
  return (
    <UsDateInput
      aria-label={label}
      value={value}
      onValueChange={onChange}
      aria-invalid={invalid}
      aria-describedby={invalid ? errorId : undefined}
      className="border-line focus:border-brand-600 h-9 bg-surface-muted px-2.5 text-[13px] focus:bg-surface"
    />
  );
}
